/**
 * 全屏看图浮层
 * - 点击遮罩 / ESC / 右上 X 关闭
 * - 图片自适应 max-h-[92vh] 不裁切
 * - 用 native capture 阻止 Radix Dialog 的 outside-click 检测
 *
 * Lightbox        — createPortal 到 document.body，适用于页面顶层独立使用
 * InlineLightbox  — 不用 portal，直接内联渲染，适用于嵌套在 Radix Dialog 内部
 *                   （Radix modal 的 inert/aria-hidden 会屏蔽 portal 节点的交互）
 */
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface Props {
  url: string | null;
  alt?: string;
  onClose: () => void;
}

function LightboxContent({ url, alt, onClose, usePortal }: Props & { usePortal: boolean }) {
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!url) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [url, handleClose]);

  useEffect(() => {
    if (!url || usePortal) return;
    const el = overlayRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopImmediatePropagation();
    el.addEventListener('pointerdown', stop, true);
    el.addEventListener('pointerup', stop, true);
    el.addEventListener('mousedown', stop, true);
    el.addEventListener('mouseup', stop, true);
    return () => {
      el.removeEventListener('pointerdown', stop, true);
      el.removeEventListener('pointerup', stop, true);
      el.removeEventListener('mousedown', stop, true);
      el.removeEventListener('mouseup', stop, true);
    };
  }, [url, usePortal]);

  if (!url) return null;

  const inner = (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-6"
      onClick={handleClose}
    >
      <button
        type="button"
        className="absolute right-4 top-4 z-[201] rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="关闭"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={url}
        alt={alt ?? ''}
        className="max-h-[92vh] max-w-full rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  return usePortal ? createPortal(inner, document.body) : inner;
}

export function Lightbox({ url, alt, onClose }: Props) {
  return <LightboxContent url={url} alt={alt} onClose={onClose} usePortal />;
}

export function InlineLightbox({ url, alt, onClose }: Props) {
  return <LightboxContent url={url} alt={alt} onClose={onClose} usePortal={false} />;
}
